import { createContext, useContext, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { Tone } from '@/lib/productRecord'
import { FEATURES } from '@/lib/featureFlags'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { hasTimeOfDay } from '@/lib/planningDates'

/**
 * The building blocks the product and patient record pages share (z8nrz7g57f,
 * z8nrz7g57g), drawn to the approved mock ("Smart-CGT – Patient & Product
 * Pages"): white cards with a hairline border on the light grey page, the blue
 * table header, caps card headings, soft tone pills, monospace DINs and MRNs,
 * underlined links, 44px buttons. The values are the mock's own, so the two
 * pages read as it does.
 */

/**
 * True inside a printed document (the patient summary print view): tables
 * wrap instead of scrolling, lists show everything, controls drop away.
 */
export const PrintModeContext = createContext(false)
export const usePrintMode = () => useContext(PrintModeContext)

/** The mock's ink: body text, secondary text, quiet text. */
export const INK = 'text-[#1D2939]'
export const MUTED = 'text-[#475467]'
export const QUIET = 'text-[#667085]'

const TONE_CLASS: Record<Tone, string> = {
  green: 'bg-[#DCFAE6] text-[#05603A]',
  amber: 'bg-[#FEF0C7] text-[#93370D]',
  blue: 'bg-[#D1E9FF] text-[#1849A9]',
  gray: 'bg-[#EAECF0] text-[#344054]',
}

export function TonePill({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-semibold',
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Grey "Not recorded": the system holds no value, which is different from an empty one. */
export function NotRecorded({ children = 'Not recorded' }: { children?: ReactNode }) {
  return <span className={QUIET}>{children}</span>
}

/** A white card on the grey page. */
export function Card({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={cn('record-card scroll-mt-20 rounded-[10px] border border-[#E4E7EC] bg-white', className)}>
      {children}
    </section>
  )
}

/** A caps card heading ("RECIPIENT & DONOR"). */
export function CapsHeading({ children }: { children: ReactNode }) {
  return <h2 className={cn('m-0 text-[13px] font-bold uppercase tracking-[0.08em]', MUTED)}>{children}</h2>
}

/** A side card: caps heading, then label/value rows. */
export function RecordCard({
  title,
  children,
  className,
  narrowLabels = false,
}: {
  title: string
  children: ReactNode
  className?: string
  /** 130px labels (the patient overview) instead of 150px (the product side cards). */
  narrowLabels?: boolean
}) {
  return (
    <Card className={cn('flex flex-col gap-3.5 p-5', className)}>
      <CapsHeading>{title}</CapsHeading>
      <dl
        className={cn(
          'm-0 grid gap-x-3 gap-y-[9px] text-sm',
          narrowLabels ? 'grid-cols-[130px_minmax(0,1fr)]' : 'grid-cols-[150px_minmax(0,1fr)]',
          INK,
        )}
      >
        {children}
      </dl>
    </Card>
  )
}

/** One label/value row. A null or empty value reads "Not recorded". */
export function Fact({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children == null || children === '' || children === false
  return (
    <>
      <dt className={MUTED}>{label}</dt>
      <dd className='m-0 min-w-0 break-words'>{empty ? <NotRecorded /> : children}</dd>
    </>
  )
}

/**
 * A main-column section: title (20px on the patient page, 18px on the
 * product page, as the mock sets them), subtitle, actions on the right, then
 * its content — padded, or edge to edge for a table.
 */
export function SectionCard({
  id,
  title,
  subtitle,
  actions,
  children,
  flush = false,
  size = 'lg',
}: {
  id?: string
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
  size?: 'lg' | 'md'
}) {
  return (
    <Card id={id} className='overflow-hidden'>
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3',
          size === 'lg' ? 'px-6 pt-5' : 'px-5 pt-[18px]',
          flush ? (size === 'lg' ? 'pb-5' : 'pb-[18px]') : 'pb-0',
        )}
      >
        <div className='flex min-w-0 flex-col gap-1'>
          <h2 className={cn('m-0 font-bold', size === 'lg' ? 'text-xl' : 'text-lg', INK)}>{title}</h2>
          {subtitle && <div className={cn('text-[13px]', MUTED)}>{subtitle}</div>}
        </div>
        {actions && <div className={cn('flex flex-wrap items-center gap-3 text-[13px]', MUTED)}>{actions}</div>}
      </div>
      <div className={flush ? '' : size === 'lg' ? 'px-6 pb-5 pt-4' : 'px-5 pb-[18px] pt-3.5'}>{children}</div>
    </Card>
  )
}

/** A wide table that scrolls sideways on a narrow screen, with the mock's blue header and zebra rows. */
export function RecordTable({
  head,
  children,
  minWidth = 900,
  numeric = [],
}: {
  head: ReactNode[]
  children: ReactNode
  minWidth?: number
  /** Header indexes that hold numbers, right-aligned. */
  numeric?: number[]
}) {
  // On paper there is no sideways scroll: the table takes the page's width and wraps.
  const print = usePrintMode()
  return (
    <div className={print ? '' : 'overflow-x-persistent'}>
      <table className={cn('w-full border-collapse', print ? 'text-xs' : 'text-[13.5px]', INK)} style={print ? undefined : { minWidth }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                scope='col'
                className={cn(
                  'bg-[#1F7BC6] align-bottom font-semibold tracking-[0.02em] text-white',
                  print ? 'px-2 py-1.5 text-[11px]' : 'whitespace-nowrap px-3.5 py-[11px] text-[12.5px]',
                  numeric.includes(i) ? 'text-right' : 'text-left',
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='[&>tr:nth-child(even)>td]:bg-[#FAFBFC] [&>tr>td]:border-b [&>tr>td]:border-[#E4E7EC] [&>tr:last-child>td]:border-b-0'>
          {children}
        </tbody>
      </table>
    </div>
  )
}

export function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  const print = usePrintMode()
  return (
    <td colSpan={colSpan} className={cn(print ? 'px-2 py-1.5 align-top' : 'px-3.5 py-[11px] align-top', className, print && 'whitespace-normal')}>
      {children}
    </td>
  )
}

/** The mock's link: underlined, in its blue. */
export const LINK = 'font-semibold text-[#175CD3] underline underline-offset-2 hover:text-[#0B4A9E]'

export function RecordLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cn(LINK, className)}>
      {children}
    </Link>
  )
}

/**
 * Whether this reader can open a product page — the gate on `/products/:id`
 * (the inventory feature and inventory.view). A link the reader cannot follow
 * only leads to No access, so callers show plain text instead.
 */
export function useCanOpenProducts(): boolean {
  const on = useFeatureFlag(FEATURES.inventory)
  const { can } = usePermission()
  return on && can(PERMISSIONS.inventoryView)
}

/** Whether this reader can open a patient page — the gate on `/patients/:mrn`. */
export function useCanOpenPatients(): boolean {
  const on = useFeatureFlag(FEATURES.patients)
  const { can } = usePermission()
  return on && can(PERMISSIONS.submissionsRead)
}

/**
 * A DIN, monospace, linking to its product. Link by product id: a DIN is several
 * products. Plain text for a reader who cannot open product pages.
 */
export function DinLink({ productId, din }: { productId: string; din: string }) {
  const canOpen = useCanOpenProducts()
  if (!canOpen) return <span className='font-mono text-[12.5px]'>{din}</span>
  return (
    <RecordLink to={`/products/${productId}`} className='font-mono text-[12.5px]'>
      {din}
    </RecordLink>
  )
}

/** The mock's 44px button: outline by default, solid blue as primary. */
export function RecordButton({
  primary = false,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { primary?: boolean }) {
  return (
    <button
      type='button'
      {...props}
      className={cn(
        'inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors disabled:cursor-not-allowed',
        primary
          ? 'border-0 bg-[#1F7BC6] text-white hover:bg-[#1A69AA]'
          : 'border border-[#D0D5DD] bg-white text-[#344054] hover:bg-[#F9FAFB] disabled:border-[#EAECF0] disabled:bg-[#EAECF0] disabled:text-[#667085]',
        className,
      )}
    />
  )
}

/** A header fact: small caps label over a value ("DOB / May 12, 1981"). */
export function HeaderFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className='flex flex-col gap-1'>
      <div className={cn('text-[11px] font-semibold uppercase tracking-[0.08em]', MUTED)}>{label}</div>
      <div className={cn('whitespace-nowrap text-[15px] font-semibold', INK)}>{value}</div>
    </div>
  )
}

const asDate = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso))

/** "10/22/2026 08:15", as the mock writes a moment. */
export function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} ${formatClock(iso)}`
}

/** "10/22/2026". A calendar date is shown as that date, never shifted by the viewer's zone. */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = asDate(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

/** "Oct 22, 2026" — the timeline's and the header's date. */
export function formatLongDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = asDate(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "08:15" — a time of day, 24-hour, as the mock writes it. */
export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/**
 * A run's scheduled date. One set without a time of day is stored as midnight
 * UTC and is that calendar day, so it is read in UTC — local time would show
 * the day before west of Greenwich. One with a time is shown in local time.
 */
export function formatScheduled(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return hasTimeOfDay(iso)
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** "10/22 08:15" — a short moment for table headers. */
export function formatShortMoment(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })} ${formatClock(iso)}`
}
